import { Box, BoxProps, Typography, TypographyProps } from '@mui/material';
import React, { ElementType, FC, forwardRef, ReactNode } from 'react';

export interface ErrorContentProps extends BoxProps<ElementType> {
  /**
   * Error Status code, eg. 404 or 500
   */
  status?: ReactNode;
  /**
   * Short message describing the error
   */
  message?: ReactNode;
  /**
   * Longer description of the error
   */
  description?: ReactNode;
  /**
   * Actions that user can interact with
   */
  actions?: ReactNode;
  /**
   * Stacktrace of an error
   */
  stacktrace?: ReactNode;
  /**
   * Properties passed to status code
   */
  statusProps?: TypographyProps<ElementType>;
  /**
   * Properties passed to message
   */
  messageProps?: TypographyProps<ElementType>;
  /**
   * Properties passed to description
   */
  descriptionProps?: TypographyProps<ElementType>;
  /**
   * Properties passed to wrapper of actions
   */
  actionsProps?: BoxProps<ElementType>;
  /**
   * Properties passed to code stacktrace
   */
  stacktraceProps?: BoxProps<ElementType>;
}

/**
 * Component that renders an information about error
 */
export const ErrorContent: FC<ErrorContentProps> = forwardRef(
  (
    {
      sx,
      children,
      status,
      message,
      actions,
      description,
      stacktrace,
      statusProps,
      messageProps,
      descriptionProps,
      actionsProps,
      stacktraceProps,
      ...rest
    },
    ref,
  ) => {
    const { sx: actionsSx, ...restActions } = actionsProps ?? {};
    const { sx: stacktraceSx, ...restStacktrace } = stacktraceProps ?? {};

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 2, md: 4 },
          textAlign: 'center',
          margin: '0 auto',
          width: '600px',
          maxWidth: '100%',
          p: 2,
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        {status && (
          <Typography component="h1" variant="display" data-testid="ErrorContent.status" {...statusProps}>
            {status}
          </Typography>
        )}
        {message && (
          <Typography component="h2" variant="h3" data-testid="ErrorContent.message" {...messageProps}>
            {message}
          </Typography>
        )}
        {description && (
          <Typography data-testid="ErrorContent.description" {...descriptionProps}>
            {description}
          </Typography>
        )}
        {children}
        {actions && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'center',
              ...actionsSx,
            }}
            data-testid="ErrorContent.actions"
            {...restActions}
          >
            {actions}
          </Box>
        )}
        {stacktrace && (
          <Box
            component="code"
            sx={{
              display: 'block',
              border: 1,
              borderColor: 'divider',
              whiteSpace: 'pre',
              overflow: 'auto',
              py: 1,
              px: 2,
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: '11px',
              lineHeight: '18px',
              textAlign: 'left',
              alignSelf: 'stretch',
              ...stacktraceSx,
            }}
            data-testid="ErrorContent.stacktrace"
            {...restStacktrace}
          >
            {stacktrace}
          </Box>
        )}
      </Box>
    );
  },
);
